/**
 * @category Home / 2.Service Now Buttons
 * @subcategory API
 * @module api-serviceNow
 */
import { ccpClient, stopTaskClient, emailnfo, sendEmail, contactCounters } from '@/api/axios';
import type { IContact } from '@/types/contact';
import type { IGetLineDropCallerId, ISolicitError } from '@/types/serviceNow';
import type { IProblemPadre } from '@/types/webSocket';
import { showErrorToastMessage, showSuccessToastMessage } from '@/utils/toastMessage';
import { useAgentStore } from '@/stores/agent';
import { storeToRefs } from 'pinia';

// Interfaccia per i dati email ricevuti dalla GET
export interface IEmailInfo {
  numero_ticket: string;
  sender: string;
  CClist: Array<{
    Address: string;
    DisplayName: string;
  }>;
  oggetto: string;
  s3Path: string;
  contact_id: string;
  customer_email: string;
  attachments?: Array<string>;
}

// Interfaccia per l'invio email (POST)
export interface ISendEmailData {
  numero_ticket_from_fe: string;
  body_from_fe: string;
  cc_from_fe: string[];
}

// Interfaccia per supporto tecnico (POST)
export interface ISupportTecnicoData {
  short_description: string;
  description: string;
  frazionario: string;
  codUser: string;
  blocked?: boolean;
}
export interface ISupportTecnicoResponse {
  ticketNumber: string;
  ticketMex: string;
}


export const getCallerId = async (
  contactId: string,
  Matricola: string
): Promise<string> => {
  try {
    const params: Record<string, string> = { Frazionario: '' };
    if (Matricola) {
      params.UID = Matricola;
    }
    if (contactId) {
      params.contactid = contactId;
    }
    const { data } = await ccpClient.get<{ sys_id: string }>('/ui/checkfrazionario', { params });

    return data.sys_id;

  } catch (error) {
    return '';
  }
};

export const getFractionalId = async (
  currentContact: IContact
): Promise<string> => {
  try {
    const { contactId, Frazionario } = currentContact;

    const params: Record<string, string> = {};
    if (Frazionario) {
      params.Frazionario = Frazionario;
    }
    if (contactId) {
      params.contactid = contactId;
    }
    const { data } = await ccpClient.get<{ sys_id: string }>('/ui/checkfrazionario', { params });

    return data.sys_id;
  } catch (error) {
    return '';
  }
};

export const getLineDropCallerId = async (
  callerId: string
): Promise<{
  getLineDropCallerIdResp: IGetLineDropCallerId
}> => {
  const { data } = await ccpClient.get<IGetLineDropCallerId>('/ui/nocaller',
    {
      params: {
        UID: callerId
      }
    });

  return { getLineDropCallerIdResp: data };
};


/**
 * @function postLineDrop
 * @async
 * @param {IContact} currentContact
 * @returns {Promise<void>}
 * @desc Opens a ritm on service now when the connection with the contact is interrupted
 *
 * **Invoked by:**
 * - ##### [lineDrop() - LineDrop.vue](module-component-LineDrop.html#)
 */
export const postLineDrop = async (
  currentContact: IContact
): Promise<void> => {
  const { Frazionario, contactPDL, contactId, currentQueue, ticketId = null, ticketNumber = null, channel = null } = currentContact;
  let body = {};
  if (channel && channel === 'CHAT') {
    body = {
      Frazionario: Frazionario,
      Pdl: contactPDL,
      ContactId: contactId,
      coda: currentQueue,
      ticketID: ticketId,
      ticketNumber: ticketNumber,
      channel: channel
    };
  } else {
    body = {
      Frazionario: Frazionario,
      Pdl: contactPDL,
      ContactId: contactId,
      coda: currentQueue,
      ticketID: ticketId,
      ticketNumber: ticketNumber
    };
  }
  const ticketMex = `Caduta linea tracciata correttamente${ticketId ? ` - Ticket: ${ticketId}` : ''}`;

  console.debug('[CADUTA LINEA] POST /ticket/cadutalinea', { ContactId: contactId, coda: currentQueue, channel });
  try {
    await ccpClient.post<{ ticketMex: string }>('/ticket/cadutalinea', body);
    console.debug('[CADUTA LINEA] POST /ticket/cadutalinea success');
  } catch (error) {
    console.error('[CADUTA LINEA] POST /ticket/cadutalinea failed', error);
    throw error;
  }

  showSuccessToastMessage(ticketMex);
};

export const postSolicit = async (
  currentContact: IContact,
  selectedTicket: { ticketId: string, ticketNumber: string, tabName: string, is_supporto_tecnico?: boolean }
): Promise<void> => {
  try {
    const { ticketId, ticketNumber, tabName, is_supporto_tecnico = false } = selectedTicket;
    const { Frazionario, contactId, currentQueue } = currentContact;
    let body;
    if (tabName === 'serviceAreaTickets') {
      const { agentFrazionario } = storeToRefs(useAgentStore());
      body = {
        ticketNumber,
        frazionario: agentFrazionario.value,
        coda: currentQueue,
        ContactId: contactId,
        is_supporto_tecnico: is_supporto_tecnico
      };
    } else {
      body = {
        ticketNumber,
        frazionario: Frazionario,
        coda: currentQueue,
        ContactId: contactId,
        is_supporto_tecnico: is_supporto_tecnico
      };
    }


    const { data } = await ccpClient.post<{ ticketMex: string }>(`/ticket/sollecito/${ticketId}`, body);

    const { ticketMex } = data;

    showSuccessToastMessage(`${ticketNumber}: ${ticketMex}`);
  } catch (error) {
    const { response } = error as ISolicitError;

    showErrorToastMessage(response.data.ticketMex);
  }
};

/**
 * @function postProblemPadre
 * @async
 * @param {IContact} currentContact
 * @param {IProblemPadre} problemPadre
 * @returns {Promise<void>}
 * @desc Open a Problem Padre ticket on service now
 *
 * **Invoked by:**
 * - ##### [openIncident() - OpenIncident.vue](module-component-OpenIncident.html#)
 */
export const postProblemPadre = async (
  currentContact: IContact,
  problemPadre: IProblemPadre
): Promise<void> => {
  try {
    const {
      Frazionario,
      contactPDL,
      contactId,
      currentQueue,
      Telefono,
      phoneNumber,
      CodUser,
      channel
    } = currentContact;
    const { handleServizio, handleCategoria, handleDiffuso } = problemPadre;

    const description = `contactId: ${contactId}, coda: ${currentQueue}, Frazionario: ${Frazionario}, PDL: ${contactPDL}, numero di telefono: ${phoneNumber || Telefono}`;

    const body = {
      Frazionario,
      handleServizio,
      handleCategoria,
      handleDiffuso,
      Description: description,
      ContactId: contactId === 'noContact' ? '' : contactId,
      ContactType: channel === 'CHAT' ? 'chat' : 'ivr',
      callerId: CodUser,
      coda: currentQueue
    };

    const { data } = await ccpClient.post<{ ticketNumber: string }>('/ticket/problempadre', body);

    const { ticketNumber } = data;

    showSuccessToastMessage(`${handleDiffuso}: Creato ticket ${ticketNumber}`);
  } catch (error) {
    const { response } = error as ISolicitError;

    showErrorToastMessage(response.data.ticketMex);
  }
};

export const updateTicket = async (
  currentContact: IContact
): Promise<void> => {
  const { contactId, ticketId, ticketNumber, agentEmail } = currentContact;

  currentContact.chimeMeeting = {};

  const body = {
    ContactId: contactId,
    TicketNumber: ticketNumber,
    Agent: agentEmail,
    currentContact
  };

  await ccpClient.post<void>(`/ticket/records/${ticketId}`, body);
};

export const updateVoiceTicket = async (
  currentContact: IContact
): Promise<void> => {

  await ccpClient.post<void>('/ticket/update', currentContact);
};

export const postponeTicket = async (
  ticketID: string,
  numberTicket: string,
  newPostponementEnd: string,
  toBePostponed: string,
  contactId: string,
  userAgent: string,
  work_note: string,
  isActive: string,
  exclusiveOwnership: boolean
): Promise<void> => {
  try {

    const body = {
      work_note: work_note,
      toBePostponed: toBePostponed,
      data: newPostponementEnd,
      ticketID: ticketID,
      numberTicket: numberTicket,
      currentContact: contactId,
      userAgent: userAgent,
      isActive: isActive,
      exclusiveOwnership: exclusiveOwnership

    };

    const { data } = await ccpClient.post<void>('/ticket/postpone', body);

    return data;

  } catch (error) {
    console.error('Error in postponeTicket API:', error);
    showErrorToastMessage('Failed to postpone the ticket.');
    throw error;
  }
};

export const unpostponeTicket = async (
  ticketID: string,
  numberTicket: string,
  toBePostponed: string,
  contactId: string,
  userAgent: string

): Promise<void> => {
  try {
    const body = {
      toBePostponed: toBePostponed,
      ticketID: ticketID,
      numberTicket: numberTicket,
      currentContact: contactId,
      userAgent: userAgent
    };

    const { data } = await ccpClient.post<void>('/ticket/postpone', body);

    console.log('Unpostpone Ticket Response:', data);
    return data;
  } catch (error) {
    console.error('Error in unpostponeTicket API:', error);
    showErrorToastMessage('Failed to unpostpone the ticket.');
    throw error;
  }
};

export const closeTask = async (
  contactId: string,
  connectionType: string,
  ticketId: string

): Promise<void> => {
  try {
    const body = {
      contactId: contactId,
      connectionType,
      ticketId
    };
    const { data } = await stopTaskClient.post<void>('/stopcontact', body);

    console.log('Close Task Response:', data);
    return data;
  } catch (error) {
    console.log('Error in closeTask API:', error);
    throw error;
  }
};
export const getUserById = async (
  userId: string
): Promise<IContact> => {
  try {
    console.log('getUserById API call - attempting to fetch for userId:', userId);
    const { data } = await ccpClient.get<IContact>(`/ui/contact/${userId}`);
    console.log('getUserById API SUCCESS - Response:', data);
    return data;
  } catch (error) {
    console.error('getUserById API FAILED - Error:', error);
    throw error;
  }
};

export const getEmailInfo = async (
  ticketNumber: string
): Promise<IEmailInfo> => {
  try {
    const { data } = await emailnfo.get<IEmailInfo>(`/${ticketNumber}`);
    return data;
  } catch (error) {
    console.error('Error in getEmailInfo API:', error);
    throw error;
  }
};

// TODO move in a contactCounters.ts file
export const getChatCounter = async (
  frazionario: string,
  callerEmail: string,
  ticketNumber: string,
  channelType: string
): Promise<{
  getChatCounterResp: number
}> => {
  try {

    if (channelType.toLowerCase() !== 'task' && channelType.toLowerCase() !== 'chat') {
      console.error(`Invalid channelType: ${channelType}`);
      return { getChatCounterResp: 0 };
    }

    // Warn about missing parameters
    const missingParams: string[] = [];
    if (!callerEmail) {
      missingParams.push('callerEmail');
    }
    if (!frazionario) {
      missingParams.push('frazionario');
    }

    if (missingParams.length > 0) {
      console.warn(`getChatCounterResp called with missing parameters: ${missingParams.join(', ')}. Proceeding anyway...`);
    }

    const username = callerEmail ? callerEmail.split('@')[0] : '';
    const { data } = await contactCounters.get<{ fraz_count: number; caller_count: number }>('/chat', {
      params: {
        username,
        frazionario,
        ticket_number: ticketNumber
      }
    });
    return { getChatCounterResp: data.caller_count };
  } catch (error) {
    console.warn('Error while computing task counter:', error);
    return { getChatCounterResp: 0 };
  }
};

export const getTaskCounter = async (
  frazionario: string,
  callerEmail: string,
  ticketNumber: string,
  channelType: string,
  service: string,
  area: string,
  cluster: string
): Promise<{
  getTaskCounterResp: number,
  getServiceResp: number,
  getAreaResp: number,
  getClusterResp: number
}> => {
  try {
    // Validate channelType
    if (channelType.toLowerCase() !== 'task' && channelType.toLowerCase() !== 'chat') {
      console.error(`Invalid channelType: ${channelType}`);
      return {
        getTaskCounterResp: 0,
        getServiceResp: 0,
        getAreaResp: 0,
        getClusterResp: 0
      };
    }

    // Warn about missing parameters
    const missingParams: string[] = [];
    if (!callerEmail) {
      missingParams.push('callerEmail');
    }
    if (!frazionario) {
      missingParams.push('frazionario');
    }

    if (missingParams.length > 0) {
      console.warn(`getTaskCounter called with missing parameters: ${missingParams.join(', ')}. Proceeding anyway...`);
    }
    // Extract caller username given the email,
    // Unknown if not computable
    const username = callerEmail ? callerEmail.split('@')[0] : 'unknown';
    // Given a username, extract:
    // - daily contacts counter with same `frazionario` value
    // - daily contacts counter with same `caller` value
    // - daily contacts counter with same `service` value
    // - daily contacts counter with same `area` value
    // - daily contacts counter with same `cluster` value

    // /contacts/tasks
    // /contacts/chat
    // bodyç {username, frazionario}
    // response {DailyCounterFraz, DailyCounterCaller, DailyCounterService, DailyCounterArea, DailyCounterCluster}
    const { data } = await contactCounters.get<{ fraz_count: number; caller_count: number; service_count: number; area_count: number; cluster_count: number; }>('/task', {
      params: {
        username,
        frazionario,
        ticket_number: ticketNumber,
        service,
        area,
        cluster
      }
    });
    // If caller is unknown, task alert should display
    // `frazionario` counter value
    if (username === 'unknown') {
      return {
        getTaskCounterResp: data.fraz_count,
        getServiceResp: data.service_count,
        getAreaResp: data.area_count,
        getClusterResp: data.cluster_count
      };
    }
    return {
      getTaskCounterResp: data.caller_count,
      getServiceResp: data.service_count,
      getAreaResp: data.area_count,
      getClusterResp: data.cluster_count
    };
  } catch (error) {
    console.warn('Error while computing task counter:', error);
    return {
      getTaskCounterResp: 0,
      getServiceResp: 0,
      getAreaResp: 0,
      getClusterResp: 0
    };
  }
};

// TODO move in a mail.ts api file
export const sendEmailFromFe = async (
  emailData: ISendEmailData
): Promise<void> => {
  try {
    const { data } = await sendEmail.post<void>('', emailData);
    console.log('Send Email Response:', data);
    showSuccessToastMessage('Email sent successfully!');
    return data;
  } catch (error) {
    console.error('Error in sendEmail API:', error);
    showErrorToastMessage('Failed to send email.');
    throw error;
  }
};


export const updateTicketWorknote = async (
  sys_id: string,
  numberTicket: string,
  work_note: string
): Promise<void> => {
  try {
    const { data } = await ccpClient.post('/ticket/worknote/update', {
      sys_id,
      numberTicket,
      work_note
    });
    console.log('Worknote updated:', data);
  } catch (error) {
    console.error('Error updating worknote:', error);
    throw error;
  }
};

export const postSupportTecnico = async (
  supportData: ISupportTecnicoData
): Promise<ISupportTecnicoResponse> => {
  try {
    const { data } = await ccpClient.post<ISupportTecnicoResponse>(
      '/ticket/supportotecnico',
      supportData
    );
    showSuccessToastMessage(data.ticketMex || 'Ticket aperto correttamente');
    return data;
  } catch (error) {
    console.error('Error in postSupportTecnico API:', error);
    showErrorToastMessage('Errore durante la creazione del ticket');
    throw error;
  }
};
